import {
  AppBar,
  Body,
  DescriptionText,
  LoadingPage,
  NotFound,
  Title,
} from "../components";
import { ServiceList } from "../shared";
import { Box } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useTheme } from "../hooks";
import { usePortal } from "../context/portalContext";
import { useTranslation } from "react-i18next";
import { useQueryService } from "../api/queries";
import { getStorageToken } from "../utils";
import { pageColumn } from "../components/layout/container/pageColumn";

const HomePage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  // Scope and branding are resolved once for the session by PortalProvider.
  // This page used to fetch the theme itself and mirror it into context on
  // every mount, which is the re-derivation 16-15 removes.
  const { state, scope, targetServiceId } = usePortal();
  const token = getStorageToken();

  // A link made for one service names it by its CORE id. The address of a
  // service step carries the id of its row in the school's list, so the list
  // is read here only to match the two -- never for a page-name visit.
  const isServiceLink =
    state === "resolved" &&
    scope?.type === "SERVICE" &&
    !!scope.entity_id &&
    !!targetServiceId;

  const linkedList = useQueryService({
    corporateId: String(scope?.entity_id ?? ""),
    enabled: isServiceLink,
    authorisation: token || "",
    // A failed lookup has somewhere to go -- the list below -- so it is not
    // retried from behind this page's spinner. The list retries for itself.
    retry: false,
  });

  const linkedService =
    isServiceLink && Array.isArray(linkedList.data)
      ? linkedList.data.find(
          service => service.core_service_id === targetServiceId
        )
      : undefined;

  // Held back only until the lookup has answered ONCE, found or failed. The
  // list this page falls back to reads the same service list; waiting on
  // that read as well would send the page back to its spinner, unmount the
  // list, and start the read again -- for good, when the read keeps failing.
  const awaitingLinkedService = isServiceLink && !linkedList.isFetched;

  if (state === "resolving" || awaitingLinkedService) {
    return <LoadingPage />;
  }

  // Both failures keep the branding shell standing. The whole-page bail-out
  // this replaces threw the app bar away with it, so a payer whose link was
  // merely mistyped lost every cue about where they were.
  if (state === "not-found" || state === "expired" || state === "unavailable") {
    return (
      <Body>
        <AppBar theme={theme} />
        <NotFound variant={state} />
      </Body>
    );
  }

  // Replacing, so Back leaves the portal instead of bouncing off this page.
  // Relative, so the step stays under whichever address the link came in by.
  if (linkedService) {
    return <Navigate to={String(linkedService.id)} replace />;
  }

  // A link whose service cannot be found falls through to the list: the
  // payer is left somewhere they can act rather than at a dead end.
  return (
    <Body>
      <AppBar theme={theme} />
      <Box sx={pageColumn}>
        <Box sx={{ display: "grid", gap: 1, maxWidth: 640 }}>
          <Title>{t("homepage.title")}</Title>
          <DescriptionText>{t("homepage.description_1")}</DescriptionText>
          <DescriptionText>{t("homepage.description_2")}</DescriptionText>
        </Box>
        <ServiceList />
      </Box>
    </Body>
  );
};

export default HomePage;
